import { FormEvent, useEffect, useState } from 'react';
import { MdClear, MdRefresh } from 'react-icons/md';
import { MonitoringGranularity, MonitoringQuery } from '../types';
import * as Styled from './styles';

type SectionFilterProps = {
  id: string;
  filters: MonitoringQuery;
  loading: boolean;
  showGranularity?: boolean;
  showLimit?: boolean;
  maxLimit?: number;
  onApply: (filters: MonitoringQuery) => void;
  onClear: () => void;
};

export function SectionFilter({
  id,
  filters,
  loading,
  showGranularity = false,
  showLimit = false,
  maxLimit = 50,
  onApply,
  onClear,
}: SectionFilterProps) {
  const [draftFilters, setDraftFilters] = useState(filters);
  const [error, setError] = useState('');

  useEffect(() => setDraftFilters(filters), [filters]);

  const updateFilter = <Key extends keyof MonitoringQuery>(key: Key, value: MonitoringQuery[Key]) => {
    setDraftFilters(current => ({ ...current, [key]: value }));
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (draftFilters.data_inicio > draftFilters.data_fim) {
      setError('A data inicial deve ser anterior ou igual à data final.');
      return;
    }

    setError('');
    onApply({ ...draftFilters });
  };

  const handleClear = () => {
    setError('');
    onClear();
  };

  return (
    <Styled.FilterPanel onSubmit={handleSubmit} aria-label="Filtros desta seção">
    <Styled.FilterRow>
      <Styled.Separator />
      <Styled.FilterFields>
        <Styled.Field>
          <label htmlFor={`${id}-start-date`}>Data inicial</label>
          <input
            id={`${id}-start-date`}
            type="date"
            required
            value={draftFilters.data_inicio}
            max={draftFilters.data_fim}
            onChange={event => updateFilter('data_inicio', event.target.value)}
          />
        </Styled.Field>
        <Styled.Field>
          <label htmlFor={`${id}-end-date`}>Data final</label>
          <input
            id={`${id}-end-date`}
            type="date"
            required
            value={draftFilters.data_fim}
            min={draftFilters.data_inicio}
            onChange={event => updateFilter('data_fim', event.target.value)}
          />
        </Styled.Field>
        {showGranularity && (
          <Styled.Field>
            <label htmlFor={`${id}-granularity`}>Granularidade</label>
            <select
              id={`${id}-granularity`}
              value={draftFilters.granularidade}
              onChange={event => updateFilter('granularidade', event.target.value as MonitoringGranularity)}
            >
              <option value="auto">Automática</option>
              <option value="hora">Hora</option>
              <option value="dia">Dia</option>
              <option value="semana">Semana</option>
              <option value="mes">Mês</option>
            </select>
          </Styled.Field>
        )}
        {showLimit && (
          <Styled.Field>
            <label htmlFor={`${id}-limit`}>Itens no ranking</label>
            <input
              id={`${id}-limit`}
              type="number"
              required
              min={1}
              max={maxLimit}
              value={draftFilters.limite}
              onChange={event => updateFilter('limite', Number(event.target.value))}
            />
          </Styled.Field>
        )}
        <Styled.SubmitButton type="submit" disabled={loading}>
          <MdRefresh className={loading ? 'spinning' : undefined} aria-hidden="true" />
          {loading ? 'Atualizando...' : 'Aplicar'}
        </Styled.SubmitButton>
        <Styled.ClearButton type="button" disabled={loading} onClick={handleClear}>
          <MdClear aria-hidden="true" />
          Limpar filtro
        </Styled.ClearButton>
      </Styled.FilterFields>
    </Styled.FilterRow>
      {error && <Styled.FormError role="alert">{error}</Styled.FormError>}
    </Styled.FilterPanel>
  );
}
